import React, { useEffect, useState } from "react";
import {
    Card,
    CardBody,
    Row,
    Col,
    Table,
    Input,
    Button,
    Badge,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter
} from "reactstrap";
import {
    getAllEnquiriesApi,
    getAllUsersApi,
    assignEnquiryApi
} from "../../api/apiUrlConst";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useTheme } from "../../context/ThemeContext";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const ITEMS_PER_PAGE = 10;

const AdminEnquiries: React.FC = () => {
    const [enquiries, setEnquiries] = useState<any[]>([]);
    const [filtered, setFiltered] = useState<any[]>([]);
    const [coordinators, setCoordinators] = useState<any[]>([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [coordinatorFilter, setCoordinatorFilter] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");
    const { theme } = useTheme();
    const isDark = theme === "dark";
    const [assignModal, setAssignModal] = useState(false);
    const [selectedEnquiry, setSelectedEnquiry] = useState<any>(null);
    const [dateFilter, setDateFilter] = useState("");
    const clearFilters = () => {
  setSearch("");
  setStatusFilter("");
  setCoordinatorFilter("");
  setFromDate("");
  setToDate("");
  setDateFilter("");
};

    // 🔥 Keep as string (GUID)
    const [selectedCoordinator, setSelectedCoordinator] = useState("");

    const navigate = useNavigate();
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        const enq = await getAllEnquiriesApi();
        const users = await getAllUsersApi();

        const coordinatorList = users.filter(
            (u: any) => u.role === "Coordinator"
        );

        setEnquiries(enq);
        setFiltered(enq);
        setCoordinators(coordinatorList);
    };

    // 🔍 Filtering Logic
  useEffect(() => {
  let data = enquiries;

  const today = new Date();

  if (search) {
    data = data.filter(
      (e) =>
        e.name.toLowerCase().includes(search.toLowerCase()) ||
        e.eid.toLowerCase().includes(search.toLowerCase())
    );
  }

  if (statusFilter) {
    data = data.filter((e) => e.status === statusFilter);
  }

  if (coordinatorFilter) {
    data = data.filter((e) => e.assignedToName === coordinatorFilter);
  }

  // QUICK DATE FILTER
  if (dateFilter) {
    data = data.filter((e) => {
      const enquiryDate = new Date(e.submittedAt);

      if (dateFilter === "today") {
        return enquiryDate.toDateString() === today.toDateString();
      }

      if (dateFilter === "thisWeek") {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay());
        return enquiryDate >= start && enquiryDate <= today;
      }

      if (dateFilter === "lastWeek") {
        const start = new Date(today);
        start.setDate(today.getDate() - today.getDay() - 7);

        const end = new Date(today);
        end.setDate(today.getDate() - today.getDay() - 1);

        return enquiryDate >= start && enquiryDate <= end;
      }

      if (dateFilter === "thisMonth") {
        return (
          enquiryDate.getMonth() === today.getMonth() &&
          enquiryDate.getFullYear() === today.getFullYear()
        );
      }

      if (dateFilter === "lastMonth") {
        const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

        return (
          enquiryDate.getMonth() === lastMonth.getMonth() &&
          enquiryDate.getFullYear() === lastMonth.getFullYear()
        );
      }

      return true;
    });
  }

  if (fromDate) {
    data = data.filter(
      (e) => new Date(e.submittedAt) >= new Date(fromDate)
    );
  }

  if (toDate) {
    data = data.filter(
      (e) => new Date(e.submittedAt) <= new Date(toDate)
    );
  }

  setFiltered(data);
  setCurrentPage(1);
}, [
  search,
  statusFilter,
  coordinatorFilter,
  fromDate,
  toDate,
  dateFilter,
  enquiries,
]);

    // 📄 Pagination
    const indexOfLast = currentPage * ITEMS_PER_PAGE;
    const indexOfFirst = indexOfLast - ITEMS_PER_PAGE;
    const currentItems = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "New":
                return <Badge color="primary">New</Badge>;
            case "Assigned":
                return <Badge color="warning">Assigned</Badge>;
            case "Joined":
                return <Badge color="success">Joined</Badge>;
            default:
                return <Badge>{status}</Badge>;
        }
    };

    const openAssignModal = (enquiry: any) => {
        setSelectedEnquiry(enquiry);
        setSelectedCoordinator(""); // reset
        setAssignModal(true);
    };

   const handleAssign = async () => {
    try {
        if (!selectedCoordinator) {
            toast.warning("Please select a coordinator");
            return;
        }

        const response = await assignEnquiryApi(
            selectedEnquiry.eid,
            selectedCoordinator
        );

        // ✅ Show backend message
        toast.success(response.data.message);

        setAssignModal(false);
        setSelectedCoordinator("");
        loadData();
    } catch (error: any) {
        console.error("Assignment failed", error);

        toast.error(
            error?.response?.data?.message || 
            "Failed to assign enquiry"
        );
    }
};

    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filtered);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Enquiries");

        const buffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array"
        });

        const blob = new Blob([buffer], {
            type: "application/octet-stream"
        });

        saveAs(blob, "AdminEnquiries.xlsx");
    };

    return (
        <>
  {/* ===== HEADER ===== */}
  <div
    className="p-4 mb-4"
    style={{
      background: "linear-gradient(90deg, #374151, #093d92)",
      borderRadius: "16px",
      color: "#fff",
    }}
  >
    <h3 className="fw-bold mb-1">Admin Enquiry Management</h3>
    <small>Manage, assign and track all enquiries</small>
  </div>

  <Row className="mb-4 g-4">
  {[
    { label: "Total", value: enquiries.length, icon: "📊" },
    { label: "New", value: enquiries.filter(e => e.status === "New").length, icon: "🆕" },
    { label: "Assigned", value: enquiries.filter(e => e.status === "Assigned").length, icon: "📌" },
    { label: "Joined", value: enquiries.filter(e => e.status === "Joined").length, icon: "🎓" }
  ].map((item, i) => (
    <Col md={3} key={i}>
      <Card
        className="border-0"
        style={{
          borderRadius: "16px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)"
        }}
      >
        <CardBody className="d-flex justify-content-between align-items-center">
          <div>
            <small className="text-muted">{item.label}</small>
            <h3 className="fw-bold mb-0">{item.value}</h3>
          </div>
          <div
            style={{
              fontSize: "26px",
              background: "#f1f3ff",
              padding: "14px",
              borderRadius: "50%"
            }}
          >
            {item.icon}
          </div>
        </CardBody>
      </Card>
    </Col>
  ))}
</Row>

<Card
  className="border-0 shadow-sm mb-4"
  style={{ borderRadius: "16px" }}
>
  <CardBody>

<div
  style={{
    display: "flex",
    flexWrap: "wrap",
    gap: "12px",
    alignItems: "center"
  }}
>

  {/* Search */}
  <Input
    placeholder="🔍 Search Name or EID"
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    className="rounded-pill shadow-sm"
    style={{ width: "230px" }}
  />

  {/* Status */}
  <Input
    type="select"
    value={statusFilter}
    onChange={(e) => setStatusFilter(e.target.value)}
    className="rounded-pill shadow-sm"
    style={{ width: "170px" }}
  >
    <option value="">All Status</option>
    <option>New</option>
    <option>Assigned</option>
    <option>Joined</option>
  </Input>

  {/* Coordinator */}
  <Input
    type="select"
    value={coordinatorFilter}
    onChange={(e) => setCoordinatorFilter(e.target.value)}
    className="rounded-pill shadow-sm"
    style={{ width: "200px" }}
  >
    <option value="">All Coordinators</option>
    {coordinators.map((c) => (
      <option key={c.userId}>{c.name}</option>
    ))}
  </Input>

  {/* From Date */}
  <Input
    type="date"
    value={fromDate}
    onChange={(e) => setFromDate(e.target.value)}
    className="rounded-pill shadow-sm"
    style={{ width: "160px" }}
  />

  {/* To Date */}
  <Input
    type="date"
    value={toDate}
    onChange={(e) => setToDate(e.target.value)}
    className="rounded-pill shadow-sm"
    style={{ width: "160px" }}
  />

  {/* Date Quick Filter */}
  <Input
    type="select"
    value={dateFilter}
    onChange={(e) => setDateFilter(e.target.value)}
    className="rounded-pill shadow-sm"
    style={{ width: "160px" }}
  >
    <option value="">All Dates</option>
    <option value="today">Today</option>
    <option value="thisWeek">This Week</option>
    <option value="lastWeek">Last Week</option>
    <option value="thisMonth">This Month</option>
    <option value="lastMonth">Last Month</option>
  </Input>

  {/* Clear Button */}
  <Button
    color="primary"
    className="rounded-pill px-4"
    onClick={clearFilters}
  >
    Clear
  </Button>

  {/* Export Button */}
  <Button
    className="rounded-pill px-4"
    style={{
      background: "linear-gradient(90deg,#374151,#093d92)",
      border: "none"
    }}
    onClick={exportToExcel}
  >
    Export
  </Button>

</div>

</CardBody>
</Card>

<Card
  className="border-0 shadow-sm"
  style={{ borderRadius: "16px" }}
>
  <CardBody>
    <Table
      responsive
      hover
      className="align-middle"
      style={{ borderCollapse: "separate", borderSpacing: "0 8px" }}
    >
      <thead>
        <tr style={{ background: "#f4f6fb" }}>
          <th>EID</th>
          <th>Name</th>
          <th>Course</th>
          <th>Status</th>
          <th>Assigned</th>
          <th>Date</th>
          <th>Actions</th>
        </tr>
      </thead>

      <tbody>
        {currentItems.map((e) => (
          <tr
            key={e.eid}
            style={{
              background: "#fff",
              boxShadow: "0 2px 10px rgba(0,0,0,0.03)"
            }}
          >
            <td className="fw-semibold">{e.eid}</td>
            <td>{e.name}</td>
            <td>{e.courseName}</td>
            <td>{getStatusBadge(e.status)}</td>
            <td>{e.assignedToName || "-"}</td>
            <td>{new Date(e.submittedAt).toLocaleDateString()}</td>

            <td>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  className="rounded-pill"
                  color="info"
                  onClick={() => navigate(`/admin/enquiries/${e.eid}`)}
                >
                  View
                </Button>

                <Button
                  size="sm"
                  className="rounded-pill"
                  style={{
                    background: "#667eea",
                    border: "none"
                  }}
                  onClick={() => openAssignModal(e)}
                >
                  Assign
                </Button>
              </div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
        <div className="d-flex justify-content-between align-items-center mt-3">
      <Button
        disabled={currentPage === 1}
        className="rounded-pill"
        onClick={() => setCurrentPage(currentPage - 1)}
      >
        Previous
      </Button>

      <span className="fw-semibold">
        Page {currentPage} of {totalPages}
      </span>

      <Button
        disabled={currentPage === totalPages}
        className="rounded-pill"
        onClick={() => setCurrentPage(currentPage + 1)}
      >
        Next
      </Button>
    </div>
  </CardBody>
</Card>

<Modal
  isOpen={assignModal}
  toggle={() => setAssignModal(false)}
  centered
>
  <ModalHeader
    toggle={() => setAssignModal(false)}
    style={{
      background: "linear-gradient(90deg,#374151, #093d92)",
      color: "#fff"
    }}
  >
    Assign Coordinator
  </ModalHeader>

  <ModalBody>
    <Input
      type="select"
      value={selectedCoordinator}
      onChange={(e) => setSelectedCoordinator(e.target.value)}
      className="rounded-pill"
    >
      <option value="">Select Coordinator</option>
      {coordinators.map((c) => (
        <option key={c.userId} value={c.userId}>
          {c.name}
        </option>
      ))}
    </Input>
  </ModalBody>

  <ModalFooter>
    <Button
      className="rounded-pill px-4"
      style={{
        background: "linear-gradient(90deg, #667eea, #764ba2)",
        border: "none"
      }}
      onClick={handleAssign}
    >
      Assign
    </Button>
  </ModalFooter>
</Modal>
</>


           
    );
};

export default AdminEnquiries;